import * as React from 'react';
const { createContext, useContext, useState, useEffect } = React;
import { useLocation } from 'react-router-dom';
import { useLoading } from '@/contexts/LoadingContext';

interface LoadingStateManagerContextType {
  persistentStates: Record<string, boolean>;
  setPersistentState: (key: string, value: boolean) => void;
  clearPersistentState: (key: string) => void;
  isTransitioning: boolean;
}

const LoadingStateManagerContext = createContext<LoadingStateManagerContextType | undefined>(undefined);

export const LoadingStateManager: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [persistentStates, setPersistentStates] = useState<Record<string, boolean>>({});
  const [isTransitioning, setIsTransitioning] = useState(false);
  const location = useLocation();
  const { setLoading, clearLoading } = useLoading();

  // Handle route changes
  useEffect(() => {
    setIsTransitioning(true);
    setLoading('page-transition', true, 'Loading page...', 'page');

    const timer = setTimeout(() => {
      setIsTransitioning(false);
      clearLoading('page-transition');
    }, 300);

    return () => {
      clearTimeout(timer);
      setIsTransitioning(false);
      clearLoading('page-transition');
    };
  }, [location.pathname, setLoading, clearLoading]);

  const setPersistentState = (key: string, value: boolean) => {
    setPersistentStates(prev => ({ ...prev, [key]: value }));
  };

  const clearPersistentState = (key: string) => {
    setPersistentStates(prev => {
      const newState = { ...prev };
      delete newState[key];
      return newState;
    });
  };

  return (
    <LoadingStateManagerContext.Provider value={{
      persistentStates,
      setPersistentState,
      clearPersistentState,
      isTransitioning
    }}>
      {children}
    </LoadingStateManagerContext.Provider>
  );
};

export const useLoadingStateManager = () => {
  const context = useContext(LoadingStateManagerContext);
  if (!context) {
    throw new Error('useLoadingStateManager must be used within LoadingStateManager');
  }
  return context;
};